import ApiService from './api';

/**
 * Authentication Service
 * Handles all auth-related API requests
 */
const AuthService = {
  /**
   * Login a user
   * @param {Object} credentials - User credentials
   * @returns {Promise} - Response data
   */
  login: async (credentials) => {
    const response = await ApiService.post('/api/auth/login', credentials);
    const data = await response.json();
    return data;
  },

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} - Response data
   */
  register: async (userData) => {
    const response = await ApiService.post('/api/auth/register', userData);
    const data = await response.json();
    return data;
  },

  /**
   * Logout the current user
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  logout: async (token) => {
    const response = await ApiService.post('/api/auth/logout', {}, {
      'Authorization': `Bearer ${token}`
    });
    const data = await response.json();
    return data;
  },

  /**
   * Verify token validity
   * @param {string} token - Auth token to verify
   * @returns {Promise} - Response data
   */
  verifyToken: async (token) => {
    const response = await ApiService.get('/api/auth/verify', {
      'Authorization': `Bearer ${token}`
    });
    const data = await response.json();
    return data;
  },

  /**
   * Send OTP to a mobile number
   * @param {string} mobile - Mobile number
   * @returns {Promise} - Response data
   */
  sendOtp: async (mobile) => {
    const response = await ApiService.post('/api/auth/send-otp', { mobile });
    const data = await response.json();
    return data;
  },

  /**
   * Verify OTP
   * @param {Object} verifyData - OTP verification data
   * @returns {Promise} - Response data
   */
  verifyOtp: async (verifyData) => {
    const response = await ApiService.post('/api/auth/verify-otp', verifyData);
    const data = await response.json();
    return data;
  },

  /**
   * Guest login
   * @returns {Promise} - Response data
   */
  guestLogin: async () => {
    const response = await ApiService.post('/api/auth/guest-login', {});
    const data = await response.json();
    return data;
  },

  /**
   * Get current user profile
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getCurrentUser: async (token) => {
    const response = await ApiService.get('/api/auth/verify', {
      'Authorization': `Bearer ${token}`
    });
    const data = await response.json();
    return data;
  },

  /**
   * Get user profile
   * @param {string} token - Auth token
   * @returns {Promise} - Response data
   */
  getUserProfile: async (token) => {
    const response = await ApiService.get('/api/auth/profile', {
      'Authorization': `Bearer ${token}`
    });
    const data = await response.json();
    return data;
  },

  /**
   * Update user profile
   * @param {string} token - Auth token
   * @param {Object} profileData - Profile data to update
   * @returns {Promise} - Response data
   */
  updateUserProfile: async (token, profileData) => {
    const response = await ApiService.put('/api/auth/profile', profileData, {
      'Authorization': `Bearer ${token}`
    });
    const data = await response.json();
    return data;
  },

  /**
   * Reset password request
   * @param {string} email - User email
   * @returns {Promise} - Response data
   */
  resetPasswordRequest: async (email) => {
    const response = await ApiService.post('/api/auth/reset-password-request', { email });
    const data = await response.json();
    return data;
  },

  /**
   * Reset password
   * @param {Object} resetData - Reset password data with token and new password
   * @returns {Promise} - Response data
   */
  resetPassword: async (resetData) => {
    const response = await ApiService.post('/api/auth/reset-password', resetData);
    const data = await response.json();
    return data;
  }
};

export default AuthService;
